/**
 * Il pannello delle bolle attese: la seconda strada per agganciare un arrivo.
 *
 * La prima resta quella della postazione — si scansiona un capo e il sistema
 * trova da sé l'ordine d'acquisto più vecchio che lo aspetta. Questa è
 * l'eccezione dichiarata dal prototipo: **si parte dalla bolla di carta**.
 * L'operatrice cerca il numero che ha in mano, lo apre, e l'arrivo risulta
 * agganciato come se avesse scansionato un capo.
 *
 * Due scelte, e sono quelle che rendono il pannello quello che è:
 *
 * 1. **è presentazionale.** Riceve le schede, l'arrivo agganciato e cosa fare
 *    quando se ne apre una: non conosce né la postazione, né il token, né
 *    react-query. Per questo si prova senza montare la rete;
 * 2. **il filtro è locale.** I dati sono già in pagina, e una ricerca che va in
 *    rete a ogni tasto premuto renderebbe lento il pannello proprio mentre
 *    l'operatrice ha le mani occupate. C'è anche una ragione dal lato del
 *    fornitore dei dati: `manage_purchase_orders/find` cerca su cinque campi, e
 *    DDT, fornitore e riferimento non sono fra quelli.
 *
 * Nessun conto si fa qui: righe e capi ancora attesi arrivano già decisi.
 */

import { useMemo, useState } from 'react';
import type { SchedaAcquisto } from '@errebi/shared';

/**
 * L'intestazione di una bolla, come la manda `GET /ricezione/:postazione/attesi`.
 *
 * **Non è più dichiarata qui.** Erano sette campi scritti due volte — questa e
 * `SchedaAcquisto` in `apps/api/src/receiving/ports.ts` — mantenuti a mano e
 * ignari l'una dell'altra: il giorno che il connettore avesse smesso di mandare
 * `ddt`, questo tipo avrebbe continuato a promettere una stringa e la schermata
 * avrebbe disegnato `undefined`. Adesso la forma sta in `@errebi/shared`, la
 * risposta ci passa attraverso in `api.ts`, e i due lati non possono scostarsi
 * senza che il compilatore lo dica.
 */
export type SchedaAttesa = SchedaAcquisto;

export function OrdiniAttesi({
  schede,
  arrivo,
  onApri,
}: {
  schede: readonly SchedaAttesa[];
  /** Il numero dell'arrivo già agganciato, per marcarne la bolla. */
  arrivo: string | null;
  onApri: (numero: string) => void;
}) {
  const [cerca, setCerca] = useState('');

  const trovate = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    if (q === '') return schede;
    return schede.filter((s) =>
      [s.numero, s.ddt ?? '', s.fornitore, s.riferimento ?? ''].some((campo) =>
        campo.toLowerCase().includes(q),
      ),
    );
  }, [schede, cerca]);

  return (
    <div className="attesi">
      <div className="attesi__testa">
        <span className="gesto__k">Bolle in attesa</span>
        <span className="mod__hint">
          Parti dalla bolla di carta: apri l’arrivo che hai in mano.
        </span>
      </div>

      <label className="campo">
        <span className="campo__k">Numero, DDT, fornitore o riferimento</span>
        <input
          type="search"
          value={cerca}
          onChange={(e) => setCerca(e.target.value)}
          placeholder="es. 57998"
          autoFocus
        />
      </label>

      <div className="attesi__elenco">
        {trovate.map((s) => (
          <button
            // Il numero come chiave regge perché `SorgenteAcquisti` promette un
            // documento per numero: su DecoNetwork il `po_number` **non è una
            // chiave** — il 552780 ne aveva due il 17/08/2026 — e la scelta di
            // quale mostrare si fa una volta, in `deduplicaPerNumero`, non qui.
            key={s.numero}
            className={'attesi__bolla' + (s.numero === arrivo ? ' attesi__bolla--qui' : '')}
            // marcata, non solo colorata: chi legge con la tastiera deve sapere
            // qual è l'arrivo già agganciato senza guardare lo sfondo
            aria-current={s.numero === arrivo ? 'true' : undefined}
            onClick={() => onApri(s.numero)}
          >
            <span className="attesi__num">{s.numero}</span>
            <span className="attesi__forn">{s.fornitore}</span>
            <span className="attesi__doc">
              {s.ddt ? `DDT ${s.ddt}` : 'senza DDT'}
              {s.riferimento ? ` · ${s.riferimento}` : ''}
              {giorno(s.emesso) ? ` · ${giorno(s.emesso)}` : ''}
            </span>
            <span className="attesi__conto">
              {s.righeAttese} righ{s.righeAttese === 1 ? 'a' : 'e'} · {s.capiAttesi} capi
            </span>
          </button>
        ))}

        {trovate.length === 0 && (
          <p className="attesi__vuoto">
            {schede.length === 0
              ? 'Nessuna bolla aperta nella finestra degli arrivi.'
              : 'Nessuna bolla con questo numero, DDT, fornitore o riferimento.'}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * La data di emissione in forma leggibile, o niente.
 *
 * Una data che non c'è non diventa «Invalid Date» accanto a un fornitore vero:
 * se non si sa quando la bolla è stata emessa, non si scrive.
 */
function giorno(emesso: string): string {
  if (emesso === '') return '';
  const d = new Date(emesso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('it-IT');
}
